import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  TableCell,
  TableRow,
  Divider,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { KeyValue, Query, TableHeader } from "../../../../interfaces";
import { fetchData } from "../../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../../config";
import MultiHeaderTable from "../../../MultiHeaderTable/MultiHeaderTable";
import {
  additionalHeaderColor,
  primaryHeaderColor,
  secondaryHeaderColor,
  textHeaderColor,
} from "../../../../refdata/constants";
import {
  fileNameFormat,
  reduceObj,
  sumValueArrayObj,
} from "../../../../common/services/Utilities";
import ResultPagination from "../../../../containers/ResultPagination";
import ApiDataDownload from "../../../ApiDataDownload";
import ModalDialog from "../../../ModalDialog";
import BranchOfficeDeposits from "../../../../containers/BranchOfficeDeposits";
import clsx from "clsx";
import { appName } from "./MarketPresenceTables";

const PREFIX = "MarketPresenceMSATable";

const classes = {
  heading: `${PREFIX}-heading`,
  subHeading: `${PREFIX}-subHeading`,
  firstCellDarken: `${PREFIX}-firstCellDarken`,
  firstCellDarkest: `${PREFIX}-firstCellDarkest`,
  footnote: `${PREFIX}-footnote`,
  buttonLink: `${PREFIX}-buttonLink`,
  groupHover: `${PREFIX}-groupHover`,
  group: `${PREFIX}-group`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.heading}`]: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },

  [`& .${classes.subHeading}`]: {
    fontWeight: 700,
    fontStyle: "normal",
    fontSize: "16px",
    color: "#1A4480",
    lineHeight: "16px",
    paddingBottom: "1rem",
  },

  [`& .${classes.firstCellDarken}`]: {
    minWidth: 120,
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    textAlign: "left",
  },

  [`& .${classes.firstCellDarkest}`]: {
    minWidth: 120,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    textAlign: "left",
  },

  [`& .${classes.footnote}`]: {
    color: theme.palette.primary.main,
    width: 900,
    minWidth: 900,
    fontSize: 16,
    fontWeight: "bold",
    fontStyle: "italic",
  },

  [`& .${classes.buttonLink}`]: {
    "fontWeight": "bold",
    "color": "#0000FF",
    "padding": 0,
    "marginTop": 0,
    "fontSize": "1rem",
    "&.MuiButton-root:hover, &.MuiButton-root:focus-visible": {
      backgroundColor: "#fff",
      textDecoration: "underline",
    },
  },

  [`& .${classes.groupHover}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.secondary.main,
      textDecoration: "underline",
      cursor: "pointer",
    },
  },

  [`& .${classes.group}`]: {
    textDecoration: "underline",
  },
}));

interface Props {
  appState: Query;
  searchPills: React.ReactElement;
  updateUi: (field: string, value: string) => void;
}

export default function MarketPresenceMSATables({
  appState,
  searchPills,
  updateUi,
}: Props) {
  const [tableData, setTableData] = React.useState([{}]);
  const [totals, setTotals] = React.useState([{}]);
  const [instName, setInstName] = React.useState("");
  const [recordCount, setRecordCount] = React.useState(0);
  const [fetchAgg, setFetchAgg] = React.useState(false);
  const nameOrNameHCR = appState.filters.cert ? "NAMEFULL" : "NAMEHCR";
  const [open, setOpen] = React.useState("");
  const branchOfficeModal = (
    <ModalDialog
      title={appState.ui.institutionName}
      display={open !== ""}
      setDisplay={() => setOpen("")}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          filters: { ...appState.filters, msa: open },
        }}
      />
    </ModalDialog>
  );
  const state = React.useMemo(() => {
    return {
      filters: appState.filters,
      search: appState.search,
    };
  }, [appState.filters, appState.search]);
  const [previousState, setPreviousState] = React.useState({});

  //fetch MSAs in specified CERT
  React.useEffect(() => {
    if (
      appState.filters.lastYear &&
      appState.ui.displayResults === "msas" &&
      (appState.filters.cert || appState.filters.bhcRSSD) &&
      JSON.stringify(state) !== JSON.stringify(previousState)
    ) {
      const certFilter = appState.filters.cert
        ? ` AND CERT:${appState.filters.cert}`
        : "";

      const rssdhcrFilter = appState.filters.bhcRSSD
        ? ` AND RSSDHCR:${appState.filters.bhcRSSD}`
        : "";

      let isMounted = true;
      setPreviousState(state);
      (async () => {
        await Promise.resolve(
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            }${certFilter}${rssdhcrFilter} AND MSABR:>0&fields=MSABR,MSANAMB,${nameOrNameHCR}&sort_by=MSANAMB&sort_order=ASC&limit=10000&offset=0&get_unique=MSABR`,
          ),
        )
          .then((response) => {
            if (isMounted) {
              setTableData(response.data);
              setRecordCount(response.totals.count);
              setInstName(response.data[0][nameOrNameHCR]);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setFetchAgg(true);
          });
      })();
    }
  }, [
    appState.filters.bhcRSSD,
    appState.filters.cert,
    appState.filters.lastYear,
    appState.ui.displayResults,
    nameOrNameHCR,
    previousState,
    state,
  ]);

  // Define a mapping of old keys to new keys for downloadData header
  const keyMapping: KeyValue = {
    MSANAMB: "MSA Name",
    MSABR: "MSA Code",
    sum_DEPSUMBR: "Total Deposits (Thousands USD)",
    count: "Office Count",
    mkt: "Market Share",
    all: "All Office Counts",
    sum: "All Total Deposits (Thousands USD)",
  };
  const merged = [...tableData, ...totals];
  const tableDownloadData: KeyValue[] = merged.map((item: KeyValue) => {
    const dataKey: string[] = Object.keys(item);

    const newData = dataKey
      .map((key) => {
        const arrayValue = Array.isArray(item[key]) && item[key][0];
        const numberValue =
          typeof item[key] === "number" &&
          key !== "MSABR" &&
          item[key]?.toLocaleString();
        return {
          key: keyMapping[key],
          value: numberValue || arrayValue || item[key],
        };
      })
      .filter((data) => data.key);
    return reduceObj(newData);
  });

  const downloadData = tableDownloadData.map((data) => {
    const additionalValues =
      appState.ui.institutionType === "bhcs"
        ? {
            "BHC Name": appState.ui.institutionName,
            "BHC ID": appState.filters.bhcRSSD,
          }
        : {
            "Bank Name": appState.ui.institutionName,
            "Cert #": appState.filters.cert,
          };
    return {
      "Reporting Period": `6/30/${appState.filters.lastYear}`,
      ...additionalValues,
      ...data,
    };
  });

  const importantInfoLink = appState.filters.bhcRSSD ? (
    <Button
      className={classes.buttonLink}
      onClick={() => window.scrollTo(0, document.body.scrollHeight)}
    >
      Click for important information about this data.
    </Button>
  ) : (
    <></>
  );

  React.useEffect(() => {
    if (fetchAgg) {
      const certFilter = appState.filters.cert
        ? ` AND CERT:${appState.filters.cert}`
        : "";
      const rssdhcrFilter = appState.filters.bhcRSSD
        ? ` AND RSSDHCR:${appState.filters.bhcRSSD}`
        : "";

      setFetchAgg(false);
      let isMounted = true;
      const uniqueVal = appState.filters.cert
        ? ` AND BKMO:1`
        : "&get_unique=RSSDHCR";
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND !MSANAMB:""${certFilter}${rssdhcrFilter}&agg_by=MSABR&agg_sum_fields=DEPSUMBR&sort_by=MSANAMB&sort_order=ASC&agg_limit=10000`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            } AND !MSANAMB:""&agg_by=MSABR&agg_sum_fields=DEPSUMBR&sort_by=MSANAMB&sort_order=ASC&agg_limit=10000`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            }${certFilter}${rssdhcrFilter}${uniqueVal}&fields=${nameOrNameHCR}`,
          ),
        ])
          .then((response) => {
            if (isMounted) {
              updateUi("institutionName", response[2].data[0][nameOrNameHCR]);
              const currentMarketData = response[0].data;
              const otherMarketData = response[1].data;
              //gets current market data
              let combinedData = tableData.map((dataset: KeyValue) => {
                const msa = dataset.MSABR;
                const currentData = currentMarketData.filter(
                  (data: KeyValue) => {
                    return data.MSABR === msa;
                  },
                );

                return { ...dataset, ...currentData[0] };
              });

              //gets other markets data and formats the object
              combinedData = combinedData.map((dataset: KeyValue) => {
                const msa = dataset.MSABR;
                const currentData = otherMarketData.filter((data: KeyValue) => {
                  return data.MSABR === msa;
                });

                const formattedData = currentData.map(
                  (data: KeyValue, index: number) => {
                    return {
                      all: data.count,
                      sum: data.sum_DEPSUMBR,
                      mkt:
                        dataset.sum_DEPSUMBR && data.sum_DEPSUMBR
                          ? `${(
                              (dataset.sum_DEPSUMBR / data.sum_DEPSUMBR) *
                              100
                            ).toFixed(2)}%`
                          : "0.00%",
                    };
                  },
                );
                return { ...dataset, ...formattedData[0] };
              });
              const marketSum = sumValueArrayObj(
                currentMarketData,
                "sum_DEPSUMBR",
              );
              const allSum: any = sumValueArrayObj(combinedData, "sum");
              const resultTotals: KeyValue = {
                MSANAMB: "Totals:",
                all: sumValueArrayObj(combinedData, "all"),
                sum_DEPSUMBR: marketSum,
                count: sumValueArrayObj(currentMarketData, "count"),
                sum: allSum,
                mkt: `${((marketSum / allSum) * 100).toFixed(2)}%`,
              };
              setTableData(combinedData);
              setTotals([resultTotals]);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [
    appState.filters.bhcRSSD,
    appState.filters.cert,
    appState.filters.lastYear,
    fetchAgg,
    nameOrNameHCR,
    tableData,
    updateUi,
  ]);

  const paginatedData = tableData.slice(
    (appState.pagination.pageNumber - 1) * appState.pagination.resultLimit,
    appState.pagination.resultLimit * appState.pagination.pageNumber,
  );

  const [groupHover, setGroupHover] = React.useState("");

  const tableCellProps = (msaName: string) => {
    const onClickFn = (e: any) => {
      if (e.key && e.key !== "Enter") {
        return;
      }
      if (appState.filters.bhcRSSD && appState.filters.lastYear) {
        setOpen(msaName);
      }
      if (appState.filters.cert && appState.filters.lastYear) {
        setOpen(msaName);
      }
    };
    return {
      onMouseEnter: () => setGroupHover(msaName),
      onMouseLeave: () => setGroupHover(""),
      onFocus: () => setGroupHover(msaName),
      onBlur: () => setGroupHover(""),
      onClick: onClickFn,
      onKeyDown: onClickFn,
      tabIndex: 0,
      className: clsx(
        groupHover === msaName ? classes.groupHover : classes.group,
      ),
    };
  };

  const table: React.ReactElement[] =
    recordCount > 0
      ? paginatedData.map((data: KeyValue, int: number) => (
          <TableRow id={data.MSANAMB} key={`${data.MSANAMB}_${int}`}>
            <TableCell
              colSpan={1}
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
              style={{
                textAlign: "left",
              }}
            >
              {data.MSANAMB}
            </TableCell>
            <TableCell
              colSpan={1}
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
            >
              {data.MSABR}
            </TableCell>
            <TableCell
              {...tableCellProps(data.MSANAMB)}
              style={{ color: "#0000FF" }}
            >
              {data.count?.toLocaleString()}
            </TableCell>
            <TableCell
              {...tableCellProps(data.MSANAMB)}
              style={{ textAlign: "right", color: "#0000FF" }}
            >
              {data.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell
              colSpan={1}
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
              style={{
                textAlign: "center",
                backgroundColor: int % 2 && int !== 0 ? "f7f7f7" : "#ededed",
              }}
            >
              {data.all?.toLocaleString()}
            </TableCell>
            <TableCell
              colSpan={1}
              className={
                int % 2 !== 0
                  ? classes.firstCellDarken
                  : classes.firstCellDarkest
              }
              style={{
                textAlign: "right",
                backgroundColor: int % 2 && int !== 0 ? "f7f7f7" : "#ededed",
              }}
            >
              {data.sum?.toLocaleString()}
            </TableCell>
            <TableCell
              style={{
                textAlign: "center",

                color: primaryHeaderColor,
              }}
            >
              {data.mkt}
            </TableCell>
          </TableRow>
        ))
      : [
          <tr key="no-results">
            <td colSpan={7}>
              Double check your search criteria for errors and try your search
              again.
            </td>
          </tr>,
        ];
  const tableTotals: React.ReactElement[] = totals.map(
    (data: KeyValue, int: number) => (
      <TableRow id={data.MSANAMB} key={`${data.MSANAMB}_${int}`}>
        <TableCell
          colSpan={1}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            textAlign: "left",
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {data.MSANAMB}
        </TableCell>
        <TableCell
          colSpan={1}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        ></TableCell>
        <TableCell
          style={{
            textAlign: "center",
            backgroundColor: secondaryHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {data.count?.toLocaleString()}
        </TableCell>
        <TableCell
          style={{
            textAlign: "right",
            backgroundColor: secondaryHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {data.sum_DEPSUMBR?.toLocaleString()}
        </TableCell>
        <TableCell
          colSpan={1}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            textAlign: "center",
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {data.all?.toLocaleString()}
        </TableCell>
        <TableCell
          colSpan={1}
          className={
            int % 2 !== 0 ? classes.firstCellDarken : classes.firstCellDarkest
          }
          style={{
            textAlign: "right",
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {data.sum?.toLocaleString()}
        </TableCell>
        <TableCell
          style={{
            textAlign: "center",
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            fontWeight: "bold",
          }}
        >
          {data.mkt}
        </TableCell>
      </TableRow>
    ),
  );

  const tableHeaders: TableHeader[][] = [
    [
      {
        title: " ",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
      {
        title: instName,
        styles: {
          backgroundColor: secondaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
      {
        title: "All FDIC-Insured Institutions",
        styles: {
          backgroundColor: additionalHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
      {
        title: " ",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
        },
        colSpan: 2,
      },
    ],
    (() => {
      const showAsterisks = appState.filters.bhcRSSD;

      const headerValues = [
        { title: "Office Count", styles: { width: 100 } },
        {
          title: "Total Deposits (Thousands USD)",
          styles: { width: 120, textAlign: "right" },
        },
      ];
      const headerValuesAsterisk = [
        { title: "Office Count", styles: { width: 100 } },
        {
          title: "Total Deposits* (Thousands USD)",
          styles: { width: 120, textAlign: "right" },
        },
      ];

      const stateCountyHeader: TableHeader[] = [
        {
          title: "MSA Name",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "left",
            textIndent: "20px",
          },
          colSpan: 1,
          apiField: "",
        },
        {
          title: "MSA Code",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            width: 120,
          },
          colSpan: 1,
          apiField: "",
        },
      ];
      const institutionHeader: TableHeader[] = (
        showAsterisks ? headerValuesAsterisk : headerValues
      ).map((header: KeyValue) => {
        return {
          title: header.title,
          styles: {
            backgroundColor: secondaryHeaderColor,
            color: textHeaderColor,
            ...header.styles,
          },
          colSpan: 1,
          apiField: "",
        };
      });

      const allInsured: TableHeader[] = headerValues.map((header: KeyValue) => {
        return {
          title: header.title,
          styles: {
            backgroundColor: additionalHeaderColor,
            color: textHeaderColor,
            ...header.styles,
          },
          colSpan: 1,
          apiField: "",
        };
      });

      const marketShare: TableHeader[] = [
        {
          title: "Market Share (%)",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            width: 120,
          },
          colSpan: 1,
          apiField: "",
        },
      ];

      return [
        ...stateCountyHeader,
        ...institutionHeader,
        ...allInsured,
        ...marketShare,
      ];
    })(),
  ];
  const headingTitle = (
    <>
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          width: 700,
          minWidth: 700,
          fontSize: 26,
          fontWeight: "bold",
          whiteSpace: "nowrap",
          paddingBottom: "0.5rem",
        }}
      >
        Market Presence Results -{" "}
        {appName(appState.filters.stalpbr, appState.ui.displayResults)}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "end",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.main,
            width: 700,
            minWidth: 700,
            fontSize: 18,
            fontWeight: "bold",
            whiteSpace: "nowrap",
            paddingBottom: "1rem",
          }}
        >
          {!appState.ui.institutionName && appState.filters.cert
            ? "No Results"
            : `${
                appState.filters.bhcRSSD
                  ? "Combined Insured Subsidiaries of "
                  : ""
              }${appState.ui.institutionName}`}
        </Typography>
        <ApiDataDownload
          app="SOD"
          apiData={[
            {
              appState,
              fileName: `${fileNameFormat("Market_Presence_MSAs")}.csv`,
              apiPath: "sod",
              text: "Download Results Data - formatted",
            },
          ]}
          downloadData={downloadData}
        />
      </Box>
    </>
  );
  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}

      <Root>
        <ResultPagination count={recordCount}>
          {importantInfoLink}
          {branchOfficeModal}
          <MultiHeaderTable
            tableBody={
              <>
                {table}
                {recordCount > 0 && tableTotals}
              </>
            }
            tableHeaders={tableHeaders}
          />
        </ResultPagination>
      </Root>
    </>
  );
}
